<!--
  ZoneTable part — one column's <th>: its label, tooltip and sort control. INTERNAL: not a Zone
  component, no story, not in llms.txt. Rendered only by Head.vue.

  One template for every column header, whichever row it lands in: a full-height cell beside a
  group tier (`rowspan="2"`) and a label-row cell must not drift apart in layout. The default slot
  replaces the label; Head.vue relays the `#header-KEY` slot into it. Classes, `rowspan` and the
  like fall through to the <th>.
-->

<template>
  <th
    scope="col"
    :aria-sort="ariaSort"
  >
    <div class="flex min-w-0 items-center gap-1">
      <!--
        The label deliberately does NOT take `flex-1`: the Zone guideline is "icons
        should always be next to text", with sorting alone pushed to the far right by
        the sort button's own `ml-auto`. `min-w-0` still lets a long label truncate.
      -->
      <slot>
        <span class="min-w-0 truncate">{{ column.label }}</span>
      </slot>

      <ZoneIcon
        v-if="column.tooltip"
        icon="info"
        :tooltip="column.tooltip"
        class="shrink-0 text-gray-600"
      />

      <!--
        `label` is optional on TableColumn but `key` never is, so fall back to the key rather
        than interpolate "undefined" into the button's accessible name. `||` and not `??`:
        a deliberately blank header (`label: ''`, legitimate for an icon-only column — the
        visible span above renders it empty) leaves a screen reader with "Sort by " otherwise.
      -->
      <button
        v-if="column.sortable"
        type="button"
        :class="sortButtonClasses(sortedHere)"
        :aria-label="t('sort_by', { column: column.label || column.key })"
        :data-testid="`sort-${column.key}`"
        @click="$emit('toggle-sort', column)"
      >
        <ZoneIcon :icon="sortIcon" />
      </button>
    </div>
  </th>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import ZoneIcon, { type IconName } from 'zone/components/base/Icon.vue';
import { sortButtonClasses } from 'zone/components/base/table/cells';
import type { TableColumn, TableSort } from 'zone/components/base/table/types';

defineOptions({ name: 'ZoneTableHeadCell' });

const { t } = useI18n();

const props = defineProps({
  column: {
    type: Object as PropType<TableColumn>,
    required: true,
  },
  sort: {
    type: Object as PropType<TableSort | null>,
    default: null,
  },
});

defineEmits(['toggle-sort']);

const sortedHere = computed<boolean>(() => props.sort?.key === props.column.key);

const sortIcon = computed<IconName>(() => {
  if (!sortedHere.value) {
    return 'swap_vert' as IconName;
  }

  return (props.sort?.direction === 'asc' ? 'arrow_upward' : 'arrow_downward') as IconName;
});

const ariaSort = computed<string | undefined>(() => {
  if (!props.column.sortable) {
    return undefined;
  }
  if (!sortedHere.value) {
    return 'none';
  }

  return props.sort?.direction === 'asc' ? 'ascending' : 'descending';
});
</script>

<i18n lang="json">
{
  "en": {
    "sort_by": "Sort by {column}"
  }
}
</i18n>
